import { nombreMes } from '../../../compartido/formato';
import type { Fecha, Hora } from '../../../compartido/tipos';

/**
 * Fechas del lado de la web. Todo se piensa en hora de Lima (UTC-5 fijo,
 * sin horario de verano), igual que en la API, sin importar dónde esté el
 * navegador: una sesión a las 10:00 es a las 10:00 de Lima para todos.
 */

const DESFASE_LIMA_MS = -5 * 3600_000;

export function hoyEnLima(ahora: Date = new Date()): Fecha {
  return new Date(ahora.getTime() + DESFASE_LIMA_MS).toISOString().slice(0, 10);
}

export const mesDe = (fecha: Fecha) => fecha.slice(0, 7);

export function sumarMeses(mes: string, n: number): string {
  const [a, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(a!, m! - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

export function sumarDias(fecha: Fecha, n: number): Fecha {
  const [a, m, d] = fecha.split('-').map(Number);
  return new Date(Date.UTC(a!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/** "Setiembre 2026" */
export function tituloMes(mes: string): string {
  const [a, m] = mes.split('-').map(Number);
  const nombre = nombreMes(m!);
  return `${nombre[0]!.toUpperCase()}${nombre.slice(1)} ${a}`;
}

/**
 * Las celdas del mes en una grilla que empieza en lunes. Las celdas antes
 * del día 1 van vacías (null) para que el 1 caiga en su columna.
 */
export function grillaDelMes(mes: string): Array<Fecha | null> {
  const primero = `${mes}-01`;
  const [a, m] = mes.split('-').map(Number);
  const dow = new Date(Date.UTC(a!, m! - 1, 1)).getUTCDay(); // 0 = domingo
  const vacias = (dow + 6) % 7; // lunes = 0
  const celdas: Array<Fecha | null> = Array.from({ length: vacias }, () => null);
  for (let f = primero; f.startsWith(mes); f = sumarDias(f, 1)) celdas.push(f);
  return celdas;
}

/** Instante UTC de una hora de Lima. */
export function instante(fecha: Fecha, hora: Hora): Date {
  const [a, m, d] = fecha.split('-').map(Number);
  return new Date(Date.UTC(a!, m! - 1, d!, hora + 5));
}

/** "Hoy", "Mañana", "En 3 días", "En 5 horas", "En curso". Para la tarjeta de la próxima sesión. */
export function cuantoFalta(inicioEn: string, ahora: Date = new Date()): string {
  const ms = Date.parse(inicioEn) - ahora.getTime();
  if (ms <= 0) return 'En curso';
  const horas = ms / 3600_000;
  if (horas < 1) return `En ${Math.max(1, Math.round(ms / 60_000))} min`;
  if (horas < 12) return `En ${Math.round(horas)} ${Math.round(horas) === 1 ? 'hora' : 'horas'}`;
  const hoy = hoyEnLima(ahora);
  const dia = hoyEnLima(new Date(inicioEn));
  if (dia === hoy) return 'Hoy';
  if (dia === sumarDias(hoy, 1)) return 'Mañana';
  const dias = Math.round((Date.parse(`${dia}T00:00:00Z`) - Date.parse(`${hoy}T00:00:00Z`)) / 86400_000);
  return `En ${dias} días`;
}

export function sonSeguidas(horas: Hora[]): boolean {
  const o = [...horas].sort((a, b) => a - b);
  return o.every((h, i) => i === 0 || h === o[i - 1]! + 1);
}

/** mm:ss para la cuenta regresiva de la retención. */
export function mmss(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Un .ics para "Agregar a mi calendario". Un evento por tramo seguido: si
 * reservó 10, 11 y 14, son dos bloques en el calendario, como en la vida.
 * Cada hora dura 50 minutos de sesión.
 */
export function archivoCalendario(s: { id: string; fecha: Fecha; horas: Hora[] }, appUrl: string): string {
  const o = [...s.horas].sort((a, b) => a - b);
  const bloques: Array<[Hora, Hora]> = [];
  for (const h of o) {
    const ultimo = bloques.at(-1);
    if (ultimo && ultimo[1] === h) ultimo[1] = h + 1;
    else bloques.push([h, h + 1]);
  }
  const utc = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const ahora = utc(new Date());
  const eventos = bloques.map(([ini, fin], i) => {
    const fin50 = new Date(instante(s.fecha, fin).getTime() - 10 * 60_000);
    return [
      'BEGIN:VEVENT',
      `UID:${s.id}-${i}@mentorking`,
      `DTSTAMP:${ahora}`,
      `DTSTART:${utc(instante(s.fecha, ini))}`,
      `DTEND:${utc(fin50)}`,
      'SUMMARY:Sesión de mentoría',
      `DESCRIPTION:El enlace de la videollamada está en ${appUrl}/acceso`,
      'END:VEVENT',
    ].join('\r\n');
  });
  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Mentorking//ES', 'CALSCALE:GREGORIAN', ...eventos, 'END:VCALENDAR', ''].join('\r\n');
}
